import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Badge from '@mui/material/Badge';
import Loading from 'components/Loading';
import PlacementExamForm from 'components/PlacementExam/PlacementExamForm';
import color from 'utils/constants/color';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import dayjs from 'utils/dayjsConfig';
import styled from 'styled-components';
import { Card, Col, Input, Modal, Row, Dropdown, message, Button } from 'antd';
import { usePlacementExamList } from 'store/placement_exam/selectors';
import { useHasPermission } from 'utils/permissions';
import SentryWrap from 'components/Sentry';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { SettingOutlined } from '@ant-design/icons';
import { filter, isEmpty } from 'lodash';
import { Assignment } from '@mui/icons-material';
import { handleListPlacementexam, handleUpdatePlacementExam } from 'store/placement_exam/thunks';
import { updatePlacementExamList } from 'store/placement_exam/actions';

const BORDERCOLOR = ['rgb(200,109,152)', 'rgb(146,158,227)', 'rgb(80,233,200)', 'rgb(238,206,151)'];

const StableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const FilterContainer = styled.span`
  font-size: 17px;
  font-weight: 500;
  cursor: pointer;
`;

interface ArchivePlacementListProps {
  userloading?: boolean;
}

const ArchivePlacementList = ({ userloading }: ArchivePlacementListProps) => {
  const [filteredStanders, setFilteredPlacementExams] = useState<any[]>([]);
  const dispatch = useDispatch();
  const placementExamList = usePlacementExamList();
  const hasEditPlacementExam = useHasPermission('placement_exam.edit');
  const hasAssignmentManagement = useHasPermission('placement_exam.assignment_management');
  const showAlert = useAlert();

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [openAssignForm, setOpenAssignForm] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState<Record<string, any>>({});
  const [archiveLoading, setArchiveLoading] = useState<string>('');

  const archiveList = useMemo(
    () => (placementExamList || []).filter((x: any) => x?.is_archived),
    [placementExamList],
  );

  useEffect(() => {
    setFilteredPlacementExams(archiveList);
  }, [archiveList]);

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value && event.target.value.length >= 1) {
      const search = event.target.value;
      const filtered = filter(archiveList, (x: any) =>
        `${x.displayName || ''}`.toLowerCase().includes(search.toLowerCase()),
      );
      setFilteredPlacementExams(filtered);
    } else {
      setFilteredPlacementExams(archiveList);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleMenuClick = (key: string, row: any) => {
    if (key === 'edit') {
      if (!hasEditPlacementExam) {
        message.warning('You do not have permission to access this feature.');
        return;
      }
      setSelectedAssignments(row);
      setOpenAssignForm(true);
    }
  };

  const archiveHandler = async (row: any) => {
    setArchiveLoading(row?.id ?? '');
    try {
      const result = (await (dispatch as any)(
        handleUpdatePlacementExam({
          ...row,
          is_archived: !row.is_archived,
        }),
      )) as { success?: boolean; message?: string; data?: any };
      if (result?.success) {
        message.success(
          row.is_archived ? 'Exam unarchived successfully' : 'Exam archived successfully',
        );
        if (result.data) {
          dispatch(updatePlacementExamList(result.data));
        }
      } else {
        message.error((result?.message as string) || 'Failed to archive exam');
      }
    } catch (error) {
      console.log(error, 'error');
    } finally {
      setArchiveLoading('');
    }
  };

  return (
    <SentryWrap>
      <FilterContainer className="FilterHeading">
        <Card type="inner" title="Filters">
          <Row>
            <Col xs={24} lg={24} md={24} className="ColCardPadding">
              <Card style={{ width: '100%' }}>
                <Input
                  placeholder="Search by name"
                  bordered={false}
                  style={{ height: '32px' }}
                  onChange={onSearch}
                />
              </Card>
            </Col>
          </Row>
        </Card>
      </FilterContainer>

      <StableWrapper>
        <Table
          stickyHeader
          aria-label="sticky table"
          style={{
            width: '100%',
            textAlign: 'center',
            backgroundColor: '#fff',
            marginTop: 20,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell style={{ textAlign: 'left', fontSize: 16, fontWeight: '600' }}>
                Display name
              </TableCell>
              <TableCell style={{ textAlign: 'left', fontSize: 16, fontWeight: '600' }}>
                Duration
              </TableCell>
              <TableCell style={{ textAlign: 'left', fontSize: 16, fontWeight: '600' }}>
                Created at
              </TableCell>
              <TableCell style={{ textAlign: 'left', fontSize: 16, fontWeight: '600' }}>
                Edit
              </TableCell>
              <TableCell style={{ textAlign: 'left', fontSize: 16, fontWeight: '600' }}>
                Archive Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty(filteredStanders) &&
              (userloading ? (
                <Loading size={30} white={false} />
              ) : (
                <TableRow key="0" style={{ border: 0 }}>
                  <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                    No results
                  </TableCell>
                </TableRow>
              ))}
            {!isEmpty(filteredStanders) &&
              filteredStanders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell>{row && row.displayName ? row.displayName : '-'}</TableCell>
                    <TableCell
                      style={{
                        textAlign: 'left',
                        color: BORDERCOLOR[0],
                      }}
                    >
                      {row && row.duration > 0 ? `${row.duration} minutes` : '-'}
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'left',
                        color: BORDERCOLOR[0],
                        maxWidth: 380,
                      }}
                    >
                      {row?.created_on ? dayjs(row.created_on).format('YYYY-MM-DD') : '-'}
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'left',
                        color: 'black',
                        cursor: 'pointer',
                        maxWidth: 380,
                      }}
                    >
                      <Dropdown
                        menu={{
                          items: [{ key: 'edit', label: 'Edit' }],
                          onClick: ({ key }) => handleMenuClick(key, row),
                        }}
                        trigger={['click']}
                        placement="bottomLeft"
                      >
                        <SettingOutlined
                          style={{
                            fontSize: 18,
                            cursor: 'pointer',
                            color: '#1890ff',
                          }}
                        />
                      </Dropdown>
                    </TableCell>

                    <TableCell
                      style={{
                        textAlign: 'left',
                        maxWidth: 380,
                        cursor: 'pointer',
                        opacity: 1,
                      }}
                    >
                      <Button
                        disabled={row?.isDraft}
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveHandler(row);
                        }}
                        style={{ width: 100 }}
                      >
                        {row?.id === archiveLoading && <Loading size={16} white={false} />}
                        {row.is_archived ? 'Unarchive' : 'Archive'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              {!isEmpty(filteredStanders) && (
                <TablePagination
                  count={filteredStanders.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  backIconButtonProps={{
                    color: 'secondary',
                  }}
                  nextIconButtonProps={{ color: 'secondary' }}
                />
              )}
            </TableRow>
          </TableFooter>
        </Table>
      </StableWrapper>

      <Modal
        footer={null}
        title={selectedAssignments?.id ? 'Edit Assignment' : 'Create Assignment'}
        centered
        open={openAssignForm}
        destroyOnClose
        onCancel={() => {
          showAlert('Data is saved as draft.', 'info');
          setOpenAssignForm(false);
          setSelectedAssignments({});
          dispatch(handleListPlacementexam() as any);
        }}
        width="100%"
        bodyStyle={{ padding: 8 }}
        className="FilterHeading"
      >
        <PlacementExamForm
          data={selectedAssignments || {}}
          onCreateOrUpdate={async () => {
            setOpenAssignForm(false);
            setSelectedAssignments({});
            dispatch(handleListPlacementexam() as any);
          }}
        />
      </Modal>
    </SentryWrap>
  );
};

export default ArchivePlacementList;
export { ArchivePlacementList as ArchivePlacement };
